import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Input } from "./ui/input";

const uuid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);

// Address input with US address suggestions (Google Places via backend proxy, OSM fallback).
// onSelect receives { formattedAddress, city, county, state, zip, lat, lng, branch }.
export function AddressInput({ value, onChange, onSelect, placeholder, className, testid = "address-input", ...rest }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hi, setHi] = useState(-1);
  const [provider, setProvider] = useState(null);
  const session = useRef(uuid());
  const typed = useRef(false);
  const seq = useRef(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!typed.current) return;
    typed.current = false;
    const q = (value || "").trim();
    if (q.length < 3) { setItems([]); setOpen(false); return; }
    const id = ++seq.current;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/places/autocomplete", { params: { input: q, session: session.current } });
        if (id !== seq.current) return;
        setItems(data.suggestions || []); setProvider(data.provider); setOpen((data.suggestions || []).length > 0); setHi(-1);
      } catch { if (id === seq.current) { setItems([]); setOpen(false); } }
      finally { if (id === seq.current) setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [value, api]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const choose = async (it) => {
    seq.current++;
    setOpen(false); setItems([]);
    let d = it.details || null;
    if (!d && it.placeId) {
      setBusy(true);
      try { const { data } = await api.get("/places/details", { params: { placeId: it.placeId, session: session.current } }); d = data; } catch { /* fall back to text */ }
      setBusy(false);
      session.current = uuid();
    }
    const typedNo = /^\d/.test(it.text || "");
    const text = d?.formattedAddress && (d.streetNumber || !typedNo) ? d.formattedAddress : (it.text || "").replace(/, USA$/, "");
    if (d) d = { ...d, formattedAddress: text };
    onChange?.(text);
    onSelect?.(d || { formattedAddress: text });
  };

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h <= 0 ? items.length - 1 : h - 1)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); choose(items[hi]); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setOpen(false); }   // 只收起地址建议，不关闭外层弹窗
  };

  return (
    <div ref={wrapRef} className="relative" data-address-open={open && items.length > 0 ? "1" : "0"}>
      <Input value={value} onChange={(e) => { typed.current = true; onChange?.(e.target.value); }} onKeyDown={onKey}
        onFocus={() => items.length && setOpen(true)} autoComplete="off" placeholder={placeholder} className={className} data-testid={testid} {...rest} />
      {busy && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" data-testid={`${testid}-busy`} />}
      {open && items.length > 0 && (
        <ul role="listbox" data-testid={`${testid}-suggestions`} className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border border-slate-200 bg-white shadow-lift overflow-hidden fade-up">
          {items.map((it, i) => (
            <li key={it.placeId || i} role="option" aria-selected={hi === i}>
              <button type="button" data-testid={`${testid}-suggestion-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(it)} onMouseEnter={() => setHi(i)}
                className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${hi === i ? "bg-brand-50" : "hover:bg-slate-50"}`}>
                <MapPin className="h-4 w-4 mt-0.5 text-brand-500 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm text-slate-800 truncate">{it.main || it.text}</span>
                  {it.secondary && <span className="block text-[11px] text-slate-400 truncate">{it.secondary}</span>}
                </span>
              </button>
            </li>
          ))}
          <li className="px-3 py-1 text-[10px] text-slate-400 text-right border-t border-slate-100" data-no-i18n>
            {provider === "google" ? "Powered by Google" : "© OpenStreetMap contributors"}
          </li>
        </ul>
      )}
      <span className="sr-only">{zh ? "输入地址后可从联想列表选择" : "Type to see address suggestions"}</span>
    </div>
  );
}

export default AddressInput;
